import React, { useState } from 'react';
import { AlertCircle, Calendar, Phone, UserCheck, UserX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import {
  PatientDetails,
  setPatientDetails,
  setPatientType,
  setRequestMetadata,
  setExistingPatientMatch,
} from '../../redux/slices/appointmentSlice';
import { Button } from '../Button';
import { search_by_phone_birthdate } from '../../services/api';
import { PATIENT_TYPE_TO_BACKEND, PatientTypeEnum } from '../../types';
import { formatPhoneInput, PHONE_REGEX, MIN_DOB, getYesterday } from '../../utils/helpers';
import { DobField } from '../ui/DobField';
import { PaymentInformation } from './PaymentInformation';
import { AppointmentNotesField } from './AppointmentNotesField';
import { SmsConsentField } from './SmsConsentField';

type Field = keyof PatientDetails;

export const ExistingPatientForm: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { treatment, patientDetails, existingPatientMatch } = useSelector((state: RootState) => state.appointment);
  const { practice } = useSelector((s: RootState) => s.auth);
  const collectInsurance = practice?.collect_insurance_details;
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const canSearch = Boolean(
    !searching &&
    PHONE_REGEX.test(patientDetails.phone) &&
    patientDetails.birth_date &&
    patientDetails.birth_date <= getYesterday() &&
    patientDetails.birth_date >= MIN_DOB
  );

  const clearMatch = () => {
    dispatch(setExistingPatientMatch(null));
    setNotFound(false);
    setSearchError(null);
  };

  const update = (field: Field, value: string) => {
    dispatch(setPatientDetails({ ...patientDetails, [field]: value }));
    clearMatch();
  };

  const updateNotes = (value: string) => {
    dispatch(setPatientDetails({ ...patientDetails, notes: value }));
  };

  const handlePhoneChange = (value: string) => {
    update('phone', formatPhoneInput(value));
  };

  const handleSwitchToNew = () => {
    dispatch(setPatientType(PatientTypeEnum.New));
    setNotFound(false);
    setSearchError(null);
    navigate('../step-3');
  };

  const handleSearch = async () => {

    setSearching(true);
    setSearchError(null);
    setNotFound(false);
    dispatch(setExistingPatientMatch(null));

    try {
      const response = await search_by_phone_birthdate({
        phone_number: patientDetails.phone,
        birth_date: patientDetails.birth_date || ''
      });

      const attributes = response?.data?.attributes || {};

      dispatch(setExistingPatientMatch({
        id: response.data.id,
        type: response.data.type,
        firstName: attributes.first_name || '',
        lastName: attributes.last_name || '',
        email: attributes.email || '',
        phone: attributes.phone_number ? formatPhoneInput(attributes.phone_number) : '',
        birth_date: attributes.birth_date || '',
      }));

      if (response.data.type === 'patient_request') {
        dispatch(setRequestMetadata({
          request_id: response.data.attributes.request_id || '',
          requestable_id: response.data.id,
          requestable_type: 'PatientRequest'
        }));
      }
    } catch (error: any) {
      const status = error?.response?.data?.status || error?.response?.status;
      if (status === 'not_found' || status === 404) {
        setNotFound(true);
      } else {
        setSearchError('Unable to find patient. Please try again.');
      }
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-x-4 gap-y-2 items-start">
        <div className="form-control">
          <label className="label">
            <span className="label-text text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Phone Number <span className="text-error">*</span>
            </span>
          </label>
          <label className={`input input-bordered flex items-center gap-2 bg-white text-black ${patientDetails.phone && !PHONE_REGEX.test(patientDetails.phone) ? 'border-error focus-within:border-error focus-within:ring-error' : 'border-teamcare-blue focus-within:border-teamcare-blue focus-within:ring-teamcare-blue'}`}>
            <Phone size={18} className="text-gray-400" />
            <input
              type="text"
              className="grow bg-transparent text-black placeholder-gray-400"
              placeholder="(XXX)XXX-XXXX"
              value={patientDetails.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              maxLength={13}
              inputMode="numeric"
            />
          </label>
          {patientDetails.phone && !PHONE_REGEX.test(patientDetails.phone) && (
            <label className="label">
              <span className="label-text-alt text-error">Phone number must be 10 digits</span>
            </label>
          )}
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Date of Birth <span className="text-error">*</span>
            </span>
          </label>
          <DobField icon={Calendar} value={patientDetails.birth_date} onChange={(iso) => update('birth_date', iso)} />
        </div>

        <div className="md:pt-9">
          <Button
            title={searching ? 'Searching...' : 'Search'}
            onClick={handleSearch}
            disabled={!canSearch}
            className="w-full md:w-auto"
          />
        </div>
      </div>

      {searchError && (
        <div className="flex items-center text-sm text-red-600 gap-2">
          <AlertCircle size={16} />
          <span>{searchError}</span>
        </div>
      )}

      {existingPatientMatch && (
        <>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <UserCheck className="w-4 h-4 text-white translate-x-px" />
              </div>
              <h3 className="font-semibold text-green-800 text-base">Welcome back, {existingPatientMatch.firstName}!</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <div className="text-xs font-semibold text-green-700 uppercase tracking-wide">Name</div>
                <div className="text-sm font-medium text-green-900 mt-1">{existingPatientMatch.firstName} {existingPatientMatch.lastName}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-green-700 uppercase tracking-wide">Phone</div>
                <div className="text-sm font-medium text-green-900 mt-1">{existingPatientMatch.phone}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-green-700 uppercase tracking-wide">Email</div>
                <div className="text-sm font-medium text-green-900 mt-1">{existingPatientMatch.email}</div>
              </div>
            </div>
          </div>

          <div className={`grid grid-cols-1 gap-x-4 gap-y-2 ${collectInsurance ? 'md:grid-cols-2' : ''}`}>
            <AppointmentNotesField
              rows={collectInsurance ? 3 : 2}
              value={patientDetails.notes}
              onChange={updateNotes}
            />

            {collectInsurance && (
              <div className="form-control">
                <PaymentInformation />
              </div>
            )}
          </div>
        </>
      )}

      {notFound && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
            <div>
              <h3 className="font-bold text-red-800 text-base">Patient Not Found</h3>
              <p className="text-red-600 text-sm">No record found with this information</p>
            </div>
          </div>
          {treatment?.allowedPatientPosting == 'both' && (
            <div className="bg-white rounded-lg p-3 border border-red-200">
              <p className="text-red-700 text-sm mb-3">
                We couldn't find a patient record with the provided information.
                This might be your first visit to our practice.
              </p>

              <button
                onClick={handleSwitchToNew}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-300 text-sm font-medium"
              >
                <UserX className="w-4 h-4 mr-2" />
                Register as New Patient
              </button>
            </div>
          )}
        </div>
      )}

      <SmsConsentField />
    </div>
  );
};

